/**
 * Reusable box checkbox, cart drawer and cart page runtime (custom, not part of stock Horizon, 4.3.0 compatible).
 *
 * Loaded by sections/reusable-box.liquid in the places its "Show in" settings allow:
 * the cart drawer on every page except /cart, and the cart page itself. Neither cart
 * summary has a block area, so this copies the checkbox out of #reusable-box-template
 * into the summary, directly above the checkout buttons, and keeps it there.
 *
 * Guards:
 *   - Runs once per page. A second copy of the script (the theme editor re-rendering
 *     the section) exits; the first copy picks up new settings on shopify:section:load.
 *   - Inserts the checkbox only when the summary doesn't already contain one.
 *   - Horizon morphs the drawer, and re-renders the cart page section, after every
 *     cart change, which drops the checkbox. A MutationObserver puts it back with the
 *     last known state.
 *   - Saves are queued, so quick ticks and unticks reach Shopify in order and the
 *     last one wins.
 *
 * No @theme/* or @shopify/* imports, so a change to Horizon's import map can't break it.
 * Depends on stock markup: #cart-drawer, .cart-drawer__summary, .cart-page__summary,
 * .cart-summary__inner, .cart__ctas, #cart-form.
 */
(() => {
  if (window.reusableBox) return;
  window.reusableBox = true;

  const CONFIG_ID = 'reusable-box-config';
  const TEMPLATE_ID = 'reusable-box-template';
  const BOX = '.reusable-box';
  const INPUT = '.reusable-box__input';

  /** The checkout buttons, in the drawer or on the cart page. Only one of them exists on a page. */
  const ANCHORS = [
    '#cart-drawer .cart-drawer__summary > .cart__ctas',
    '.cart-page__summary .cart-summary__inner > .cart__ctas',
  ];

  let config = readConfig();
  let checked = Boolean(config?.checked);

  let frame = 0;
  let saving = Promise.resolve();
  let pending = 0;

  const observer = new MutationObserver(schedule);

  function readConfig() {
    const element = document.getElementById(CONFIG_ID);
    if (!element) return null;

    try {
      const data = JSON.parse(element.textContent || '');
      return data;
    } catch {
      return null;
    }
  }

  function schedule() {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      sync();
    });
  }

  function findAnchor() {
    for (const selector of ANCHORS) {
      const anchor = document.querySelector(selector);
      if (anchor) return anchor;
    }
    return null;
  }

  /** Makes sure the summary holds exactly one checkbox, or none when the section is gone. */
  function sync() {
    if (!config) {
      document.querySelectorAll(BOX).forEach((box) => box.remove());
      return;
    }

    const anchor = findAnchor();
    const host = anchor?.parentElement;
    if (!anchor || !host) return;

    let box = host.querySelector(`:scope > ${BOX}`);

    if (!(box instanceof HTMLElement)) {
      const template = document.getElementById(TEMPLATE_ID);
      if (!(template instanceof HTMLTemplateElement)) return;

      box = template.content.querySelector(BOX)?.cloneNode(true);
      if (!(box instanceof HTMLElement)) return;

      anchor.before(box);
    }

    paint(box);
  }

  /** @param {HTMLElement} box */
  function paint(box) {
    const input = box.querySelector(INPUT);
    if (input instanceof HTMLInputElement && input.checked !== checked) input.checked = checked;

    if (pending > 0) box.setAttribute('aria-busy', 'true');
    else box.removeAttribute('aria-busy');
  }

  /** Saves the attribute; an empty value removes it from the cart. */
  function save() {
    const value = checked ? config.value : '';
    const attribute = config.attribute;
    const root = window.Shopify?.routes?.root ?? '/';

    pending += 1;
    schedule();

    saving = saving
      .then(() =>
        fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { [attribute]: value } }),
        })
      )
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.json();
      })
      .then((cart) => {
        // Only the last queued save decides the shown state.
        if (pending === 1) checked = Boolean(cart?.attributes?.[attribute]);
      })
      .catch(() => {
        if (pending === 1) refreshFromCart();
      })
      .finally(() => {
        pending -= 1;
        schedule();
      });
  }

  /** Reads the attribute straight from Shopify, e.g. after the cart changed on another page. */
  async function refreshFromCart() {
    if (!config) return;

    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      checked = Boolean(cart?.attributes?.[config.attribute]);
    } catch {
      /* keep the last known state */
    }
    schedule();
  }

  /** @param {Event} event */
  function onChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches(INPUT) || !config) return;

    checked = input.checked;
    save();
  }

  /** @param {Event} event */
  function isOwnSection(event) {
    return event.target instanceof Element && Boolean(event.target.querySelector(`#${CONFIG_ID}`));
  }

  /** Theme editor: re-read settings and rebuild the checkbox from the fresh template. */
  function reloadFromEditor() {
    config = readConfig();
    if (config) checked = Boolean(config.checked);
    document.querySelectorAll(BOX).forEach((box) => box.remove());
    schedule();
  }

  document.addEventListener('change', onChange);

  // A page restored from the back/forward cache may show a cart that changed on another page.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshFromCart();
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (isOwnSection(event)) reloadFromEditor();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    // Unload fires before the editor removes the section, so wait a frame for it to be gone.
    if (isOwnSection(event)) requestAnimationFrame(reloadFromEditor);
  });

  // The drawer's section, or the main cart section on /cart. Section wrappers survive re-renders.
  const observed =
    document.getElementById('shopify-section-cart-drawer-section') ??
    document.querySelector('#cart-drawer') ??
    document.querySelector('.cart-page')?.closest('.shopify-section');
  if (observed) observer.observe(observed, { childList: true, subtree: true });

  sync();
})();
